/**
 * AccountOverviewBlock.tsx
 * Top of the Overview tab on /#/account/:id.
 * Hero block + 4 quick stats + 3-column mid section + DM strip.
 *
 * Brand: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7. M-PACT with hyphen.
 */
import { useEffect, useState } from "react";
import { TOP10_BOOTCAMP_SEED } from "@/data/top10-bootcamp-seed";
import { api, supabase, getCurrentTenant } from "@/lib/session";
import { ForecastCategoryPicker } from "@/components/forecast/ForecastCategoryPicker";
// Sprint 1 — Surface 1 (RULE-GATE0): import present; mount point added when gate0Refusal variable is wired
import type { ForecastBucket } from "../../../../shared/rule-forecast-1";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const GREEN = "#16A34A";
const AMBER = "#EA580C";
const SLATE = "#6B6B70";

function fmt$(n: number): string {
  if (!n) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

function compositeColor(score: number): string {
  if (score >= 70) return GREEN;
  if (score >= 40) return AMBER;
  return RED;
}

interface Props {
  accountId: string;
  accountName: string;
  industry: string;
  score: number;
  estArr: number;
  stage: string;
  greenCount: number;
  scoredCount: number;
  onCpClick: (cp: number) => void;
}

export default function AccountOverviewBlock({
  accountId,
  accountName,
  industry,
  score,
  estArr,
  stage,
  greenCount,
  scoredCount,
  onCpClick,
}: Props) {
  // Look up seed for narrative content
  const seed = TOP10_BOOTCAMP_SEED.find(
    (s) => s.id === accountId || s.account_id === accountId || s.account_name === accountName
  );

  const compositeC = compositeColor(score);

  // Real / seeded values only. No fabricated activity, dates, or counts.
  const dmTarget = 3;
  // Canonical source: decision_makers DB table. Falls back to the bootcamp
  // seed relationship_map only while the API request is in flight, so the UI
  // matches the Stakeholders tab (which reads the same endpoint).
  const seedDmCount = seed?.bootcamp_seed?.relationship_map?.length ?? 0;
  const [dbDmCount, setDbDmCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    const idNum = parseInt(accountId, 10);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    api<Array<{ id: number }>>(`/api/accounts/${idNum}/decision-makers`)
      .then((rows) => { if (!cancelled) setDbDmCount(Array.isArray(rows) ? rows.length : 0); })
      .catch(() => { if (!cancelled) setDbDmCount(null); });
    return () => { cancelled = true; };
  }, [accountId]);
  const dmCount = dbDmCount ?? seedDmCount;
  const nextStep: string | null = seed?.bootcamp_seed?.first_move ?? null;
  const dealValue = estArr || 0;

  const whereStuck: string[] = [];
  if (dmCount < dmTarget) whereStuck.push(`${dmCount}/${dmTarget} decision makers mapped`);
  if (greenCount < 7) whereStuck.push(`${10 - greenCount} checkpoints not yet green`);
  if (whereStuck.length === 0) whereStuck.push("No blockers identified");

  const recentActivity: Array<{ when: string; channel: string; outcome: string }> = [];

  // ── RULE-FORECAST-1 gate state ──────────────────────────────────────────────
  const tenant = getCurrentTenant?.();
  const gateActive = tenant?.id === 1; // pilot tenant only per spec
  const [timingPct, setTimingPct] = useState<number | null>(null);
  const [currentBucket, setCurrentBucket] = useState<ForecastBucket | null>(null);
  const [bucketLoaded, setBucketLoaded] = useState(false);
  const [bucketSaveError, setBucketSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!gateActive) {
      setBucketLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const sb = supabase();
        const idNum = parseInt(accountId, 10);
        const [{ data: acctRow }, { data: timingRows }] = await Promise.all([
          sb
            .from("accounts")
            .select("forecast_category_override")
            .eq("id", idNum)
            .maybeSingle(),
          sb
            .from("deal_timing_records")
            .select("rep_declared_timing_pct,manager_override_pct,assessed_at,is_current")
            .eq("deal_id", String(accountId))
            .eq("is_current", true)
            .order("assessed_at", { ascending: false })
            .limit(1),
        ]);
        if (cancelled) return;
        const t = timingRows && timingRows[0] ? timingRows[0] : null;
        const tpct: number | null = t
          ? (t.manager_override_pct ?? t.rep_declared_timing_pct ?? null)
          : null;
        setTimingPct(tpct);
        const cur: ForecastBucket | null =
          (acctRow?.forecast_category_override as ForecastBucket | undefined) ?? null;
        setCurrentBucket(cur);
      } catch (err) {
        console.error("[AccountOverview] forecast-gate load failed", err);
      } finally {
        if (!cancelled) setBucketLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, gateActive]);

  async function handleBucketSave(next: ForecastBucket, autoDowngraded: boolean): Promise<void> {
    setBucketSaveError(null);
    try {
      const idNum = parseInt(accountId, 10);
      const update: Record<string, unknown> = { forecast_category_override: next };
      // If the system had to downgrade the rep's pick, mark the deal for
      // manager attention so a human reviews the override-attempt history.
      if (autoDowngraded) {
        update.manager_attention_flag = true;
        update.manager_flag_reason =
          "RULE-FORECAST-1: rep selected a bucket above the timing-eligible floor";
      }
      // B-F3: use server route (role-gated manager/executive only via Wave F)
      // instead of direct Supabase client write.
      await api(`/api/accounts/${idNum}`, {
        method: "PATCH",
        body: JSON.stringify(update),
      });
      setCurrentBucket(next);
    } catch (err: any) {
      console.error("[AccountOverview] forecast save failed", err);
      setBucketSaveError(err?.message ?? "Failed to save forecast category");
    }
  }

  const advances: string[] = [];
  if (nextStep) advances.push(nextStep);
  if (greenCount < 10) advances.push(`Advance ${10 - greenCount} remaining checkpoint${10 - greenCount === 1 ? "" : "s"} to green`);
  if (dmCount < dmTarget) advances.push(`Map ${dmTarget - dmCount} more decision-maker contact${dmTarget - dmCount === 1 ? "" : "s"}`);

  return (
    <div className="space-y-5 mb-8">
      {/* Hero block */}
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 flex flex-col lg:flex-row gap-5">
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: BLACK }}>{accountName}</h2>
            {stage && (
              <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded-full" style={{ background: BLACK, color: "white" }}>
                {stage}
              </span>
            )}
            {industry && (
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-full bg-neutral-100" style={{ color: SLATE }}>
                {industry}
              </span>
            )}
          </div>
          <div className="mt-2 text-[12px]" style={{ color: SLATE }}>
            {seed?.bootcamp_seed?.why_this_account
              ? seed.bootcamp_seed.why_this_account.slice(0, 240) + (seed.bootcamp_seed.why_this_account.length > 240 ? "..." : "")
              : "Top-10 ABM target. Discovery in progress."}
          </div>
          <div className="mt-3 flex items-center gap-4 flex-wrap text-[12px]">
            <div>
              <span className="text-[10px] uppercase tracking-wide" style={{ color: SLATE }}>Deal value</span>
              <div className="font-bold tabular-nums text-base" style={{ color: BLACK }}>{fmt$(dealValue)}</div>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wide" style={{ color: SLATE }}>Expected close</span>
              <div className="font-bold tabular-nums text-base" style={{ color: SLATE }}>Not set</div>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[10px] uppercase tracking-wide" style={{ color: SLATE }}>Next step</span>
              <div className="font-bold text-base truncate" style={{ color: nextStep ? BLACK : SLATE }} title={nextStep ?? ""}>{nextStep ?? "Not set"}</div>
            </div>
          </div>

          {gateActive && bucketLoaded && (
            <div className="mt-4 pt-4 border-t border-neutral-200">
              <ForecastCategoryPicker
                compositeScore={score}
                timingPct={timingPct}
                current={currentBucket}
                onSave={handleBucketSave}
                testIdPrefix={`account-${accountId}-forecast`}
                accountId={accountId}
              />
              {bucketSaveError && (
                <div className="mt-2 text-[11px]" style={{ color: RED }}>
                  Save failed: {bucketSaveError}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Composite score badge */}
        <div className="flex flex-col items-center justify-center px-5 border-l border-neutral-100">
          <div className="text-[9px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: SLATE }}>
            Composite
          </div>
          <div className="text-5xl font-black tabular-nums" style={{ color: compositeC, lineHeight: 1 }}>
            {score}
          </div>
          <div className="text-[10px] mt-1" style={{ color: SLATE }}>composite / 100</div>
        </div>
      </div>

      {/* 4 quick stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-neutral-200 p-3">
          <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: SLATE }}>Days in stage</div>
          <div className="text-xl font-bold tabular-nums" style={{ color: SLATE }}>—</div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-3">
          <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: SLATE }}>Touches since DM</div>
          <div className="text-xl font-bold tabular-nums" style={{ color: SLATE }}>—</div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-3">
          <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: SLATE }}>Decision makers</div>
          <div className="text-xl font-bold tabular-nums" style={{ color: dmCount < dmTarget ? RED : GREEN }}>
            {dmCount}<span className="text-xs font-normal" style={{ color: SLATE }}> / {dmTarget}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-neutral-200 p-3">
          <div className="text-[9px] uppercase tracking-[0.18em]" style={{ color: SLATE }}>Green checkpoints</div>
          <div className="text-xl font-bold tabular-nums" style={{ color: greenCount >= 7 ? GREEN : AMBER }}>
            {greenCount}<span className="text-xs font-normal" style={{ color: SLATE }}> / 10</span>
          </div>
        </div>
      </div>

      {/* 3 columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Where we are */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: BLACK }}>
            Where we are
          </div>
          <div className="space-y-2">
            {recentActivity.length === 0 ? (
              <div className="text-[12px] leading-snug" style={{ color: SLATE }}>No activity logged yet. Use the Activity Log tab to record calls, emails, and meetings.</div>
            ) : recentActivity.map((a, i) => (
              <div key={i} className="text-[12px] leading-snug">
                <div style={{ color: SLATE }}>{a.when} · {a.channel}</div>
                <div style={{ color: BLACK }}>{a.outcome}</div>
              </div>
            ))}
          </div>
        </div>
        {/* Where we're stuck */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: RED }}>
            Where we're stuck
          </div>
          <ul className="space-y-1.5 text-[12px] leading-snug">
            {whereStuck.map((s, i) => (
              <li key={i} className="flex items-start gap-2" style={{ color: BLACK }}>
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: RED }} />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* What advances this */}
        <div className="bg-white rounded-xl border border-neutral-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: GREEN }}>
            What advances this
          </div>
          {advances.length === 0 ? (
            <div className="text-[12px] leading-snug" style={{ color: SLATE }}>All checkpoints green. Push for commit and timing.</div>
          ) : (
            <ul className="space-y-1.5 text-[12px] leading-snug">
              {advances.map((a, i) => (
                <li key={i} className="flex items-start gap-2" style={{ color: BLACK }}>
                  <span className="mt-0.5 text-xs font-bold" style={{ color: GREEN }}>{i + 1}.</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Checkpoints 10-dot strip */}
      <div className="bg-white rounded-xl border border-neutral-200 p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: SLATE }}>
          Checkpoints · click any to open in Opportunity DNA
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {Array.from({ length: 10 }).map((_, i) => {
            const cp = i + 1;
            const isGreen = cp <= greenCount;
            return (
              <button
                key={cp}
                onClick={() => onCpClick(cp)}
                className="flex flex-col items-center gap-1 hover:opacity-80"
                style={{ background: "transparent", border: "none", cursor: "pointer" }}
                title={`CP${cp}`}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ background: isGreen ? GREEN : i < scoredCount ? AMBER : "#C9C9CC" }}
                >
                  {cp}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Decision-makers strip */}
      {seed?.bootcamp_seed?.relationship_map && seed.bootcamp_seed.relationship_map.length > 0 && (
        <div className="bg-white rounded-xl border border-neutral-200 p-4">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-3" style={{ color: SLATE }}>
            Decision makers mapped
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {seed.bootcamp_seed.relationship_map.slice(0, 5).map((r: any, i: number) => (
              <div key={i} className="border border-neutral-100 rounded-lg p-2.5">
                <div className="text-[12px] font-bold truncate" style={{ color: BLACK }}>{r.name}</div>
                <div className="text-[10px] truncate" style={{ color: SLATE }}>{r.role}</div>
                <div className="text-[10px] mt-1 inline-block px-1.5 py-0.5 rounded" style={{
                  background: r.owner_type === "champion" ? "rgba(22,163,74,0.14)" :
                              r.owner_type === "coach" ? "#EFEFEE" :
                              r.owner_type === "fox" ? "rgba(234,179,8,0.16)" : "#EFEFEE",
                  color: BLACK,
                }}>
                  {r.owner_type || r.owner || "contact"} · warmth {r.warmth ?? "?"}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
